/*
 * FICHIER: htmlUtils.js
 * BUT: Utilitaires pour traiter et manipuler le contenu HTML
 * 
 * Ces fonctions sont utilisées pour traiter le contenu riche provenant de Strapi (CKEditor)
 * et s'assurer qu'il respecte certaines règles de sécurité et d'expérience utilisateur.
 * 
 * PROBLÈMES RÉSOLUS :
 * 1. Quand les utilisateurs créent du contenu avec des liens dans l'éditeur Strapi,
 *    ces liens s'ouvrent par défaut dans la même fenêtre, ce qui fait quitter le site.
 *    Ces fonctions modifient automatiquement tous les liens pour qu'ils s'ouvrent
 *    dans un nouvel onglet avec les attributs de sécurité appropriés.
 * 2. Quand les utilisateurs insèrent une URL YouTube via le bouton "insert media" de CKEditor,
 *    l'URL est stockée comme un lien ou un élément média qui ne s'affiche pas correctement.
 *    Cette fonction convertit automatiquement les URLs YouTube en iframes pour l'affichage.
 */

/**
 * Traite le contenu HTML pour ajouter target="_blank" et rel="noopener noreferrer" 
 * aux liens afin qu'ils s'ouvrent dans un nouvel onglet
 * 
 * POURQUOI C'EST IMPORTANT :
 * - target="_blank" : Ouvre le lien dans un nouvel onglet
 * - rel="noopener noreferrer" : Sécurise le lien contre les attaques de type "tabnabbing"
 * 
 * UTILISATION :
 * const htmlSafe = processLinksForNewTab(article.content);
 * 
 * @param {string} htmlContent - Le contenu HTML à traiter (ex: contenu d'article Strapi)
 * @returns {string} - Le contenu HTML modifié avec les liens sécurisés
 */
export const processLinksForNewTab = (htmlContent) => {
  // VÉRIFICATIONS DE BASE
  if (!htmlContent || typeof htmlContent !== 'string') {
    return htmlContent; // Retourne tel quel si pas de contenu ou pas une string
  }

  // CHOIX DE LA MÉTHODE DE TRAITEMENT
  // Si on est dans un navigateur (document disponible), utiliser la méthode DOM (plus robuste)
  if (typeof document !== 'undefined') {
    return processLinksForNewTabDOM(htmlContent);
  }

  // FALLBACK REGEX pour les environnements sans DOM (ex: Server-Side Rendering)
  // Utilise une expression régulière pour trouver et modifier les balises <a>
  return htmlContent.replace(
    /<a\s+([^>]*?)>/gi,  // Trouve toutes les balises <a> avec leurs attributs
    (match, attributes) => {
      // Vérifier si target="_blank" est déjà présent
      if (attributes.includes('target=')) {
        // Si target est déjà défini, s'assurer qu'il y a aussi rel="noopener noreferrer"
        if (!attributes.includes('rel=')) {
          return `<a ${attributes} rel="noopener noreferrer">`;
        }
        return match; // Garder tel quel si target et rel sont déjà définis
      } else {
        // Ajouter target="_blank" et rel="noopener noreferrer"
        return `<a ${attributes} target="_blank" rel="noopener noreferrer">`;
      }
    }
  );
};

/**
 * Fonction alternative utilisant le DOM pour un traitement plus robuste
 * 
 * AVANTAGES DE LA MÉTHODE DOM vs REGEX :
 * - Plus précise (pas de faux positifs avec du texte qui ressemble à du HTML)
 * - Gère mieux les attributs complexes (guillemets simples/doubles, espaces, etc.)
 * - Plus maintenable et lisible
 * - Respecte la structure HTML existante
 * 
 * @param {string} htmlContent - Le contenu HTML à traiter
 * @returns {string} - Le contenu HTML modifié
 */
export const processLinksForNewTabDOM = (htmlContent) => {
  // VÉRIFICATIONS DE BASE
  if (!htmlContent || typeof htmlContent !== 'string') {
    return htmlContent;
  }

  try {
    // CRÉATION D'UN ÉLÉMENT TEMPORAIRE POUR PARSER LE HTML
    // On utilise un div temporaire pour manipuler le HTML de façon sûre
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent; // Parse le HTML string en structure DOM

    // RECHERCHE DE TOUS LES LIENS
    const links = tempDiv.querySelectorAll('a'); // Sélectionne toutes les balises <a>
    
    // TRAITEMENT DE CHAQUE LIEN
    links.forEach((link) => {
      // AJOUTER target="_blank" seulement s'il n'est pas déjà défini
      if (!link.hasAttribute('target')) {
        link.setAttribute('target', '_blank');
      }
      
      // AJOUTER rel="noopener noreferrer" pour la sécurité
      if (!link.hasAttribute('rel')) {
        // Pas de rel existant : on l'ajoute
        link.setAttribute('rel', 'noopener noreferrer');
      } else {
        // Si rel existe déjà, s'assurer qu'il contient noopener noreferrer
        const currentRel = link.getAttribute('rel');
        if (!currentRel.includes('noopener')) {
          // Ajouter noopener noreferrer à la valeur existante
          link.setAttribute('rel', `${currentRel} noopener noreferrer`);
        }
      }
    });

    // RETOURNER LE HTML MODIFIÉ
    return tempDiv.innerHTML; // Convertit la structure DOM modifiée en string HTML
    
  } catch (error) {
    // GESTION D'ERREUR
    console.error('[processLinksForNewTabDOM] Erreur lors du traitement des liens:', error);
    return htmlContent; // Retourner le contenu original en cas d'erreur pour éviter de casser l'affichage
  }
};

/**
 * Extrait l'ID d'une vidéo YouTube à partir d'une URL
 * Supporte plusieurs formats d'URL YouTube
 * 
 * @param {string} url - L'URL YouTube (ex: https://www.youtube.com/watch?v=VIDEO_ID)
 * @returns {string|null} - L'ID de la vidéo ou null si l'URL n'est pas valide
 */
export const extractYouTubeId = (url) => {
  if (!url || typeof url !== 'string') return null;
  
  // Formats YouTube supportés :
  // - https://www.youtube.com/watch?v=VIDEO_ID
  // - https://youtu.be/VIDEO_ID
  // - https://www.youtube.com/embed/VIDEO_ID
  // - https://youtube.com/watch?v=VIDEO_ID
  // - https://m.youtube.com/watch?v=VIDEO_ID
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?#]+)/i,
    /^([a-zA-Z0-9_-]{11})$/i // ID direct (11 caractères)
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  return null;
};

/**
 * Convertit une URL YouTube en URL d'embed
 * 
 * @param {string} url - L'URL YouTube originale
 * @returns {string|null} - L'URL d'embed ou null si l'URL n'est pas valide
 */
export const convertYouTubeToEmbed = (url) => {
  const videoId = extractYouTubeId(url);
  if (!videoId) return null;
  
  return `https://www.youtube.com/embed/${videoId}`;
};

/**
 * Traite le contenu HTML pour convertir les liens YouTube en iframes embed
 * 
 * POURQUOI C'EST IMPORTANT :
 * - CKEditor stocke les URLs YouTube comme des liens ou éléments média
 * - Ces éléments ne s'affichent pas correctement dans le rendu final
 * - Cette fonction convertit automatiquement les URLs YouTube en iframes fonctionnelles
 * 
 * @param {string} htmlContent - Le contenu HTML à traiter
 * @returns {string} - Le contenu HTML avec les vidéos YouTube converties en iframes
 */
export const processVideoEmbeds = (htmlContent) => {
  // VÉRIFICATIONS DE BASE
  if (!htmlContent || typeof htmlContent !== 'string') {
    return htmlContent;
  }

  try {
    // CRÉATION D'UN ÉLÉMENT TEMPORAIRE POUR PARSER LE HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    // RECHERCHE DE TOUS LES LIENS ET ÉLÉMENTS MÉDIA
    const links = tempDiv.querySelectorAll('a');
    // Rechercher aussi dans les éléments figure, oembed, et les divs qui pourraient contenir l'URL
    const allElements = tempDiv.querySelectorAll('*');
    
    // TRAITEMENT DES LIENS YOUTUBE
    links.forEach((link) => {
      const href = link.getAttribute('href');
      if (href) {
        const embedUrl = convertYouTubeToEmbed(href);
        if (embedUrl) {
          // Créer une iframe YouTube
          const iframe = document.createElement('iframe');
          iframe.setAttribute('src', embedUrl);
          iframe.setAttribute('width', '100%');
          iframe.setAttribute('height', '400');
          iframe.setAttribute('frameborder', '0');
          iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
          iframe.setAttribute('allowfullscreen', 'true');
          iframe.style.maxWidth = '100%';
          iframe.style.aspectRatio = '16 / 9';
          
          // Créer un conteneur responsive
          const container = document.createElement('div');
          container.style.position = 'relative';
          container.style.width = '100%';
          container.style.paddingBottom = '56.25%'; // 16:9 aspect ratio
          container.style.overflow = 'hidden';
          iframe.style.position = 'absolute';
          iframe.style.top = '0';
          iframe.style.left = '0';
          iframe.style.width = '100%';
          iframe.style.height = '100%';
          
          container.appendChild(iframe);
          
          // Remplacer le lien par l'iframe
          link.parentNode.replaceChild(container, link);
        }
      }
    });

    // TRAITEMENT DES ÉLÉMENTS MÉDIA (figure, oembed, images avec lien YouTube, etc.)
    // Créer une liste des éléments déjà traités pour éviter les doublons
    const processedElements = new Set();
    
    allElements.forEach((element) => {
      // Ignorer les liens (déjà traités) et les éléments déjà traités
      if (element.tagName === 'A' || processedElements.has(element)) {
        return;
      }
      
      // Chercher des URLs YouTube dans différents attributs
      let youtubeUrl = element.getAttribute('src') || 
                       element.getAttribute('data-url') || 
                       element.getAttribute('data-src') ||
                       element.getAttribute('href');
      
      // Chercher dans le texte de l'élément (pour les oembed et figures)
      if (!youtubeUrl && element.textContent) {
        const text = element.textContent.trim();
        // Vérifier si le texte est une URL YouTube
        if (extractYouTubeId(text)) {
          youtubeUrl = text;
        }
      }
      
      // Si l'élément est une image, vérifier aussi dans le parent link
      if (!youtubeUrl && element.tagName === 'IMG') {
        const parentLink = element.closest('a');
        if (parentLink && parentLink.getAttribute('href')) {
          const parentHref = parentLink.getAttribute('href');
          if (extractYouTubeId(parentHref)) {
            youtubeUrl = parentHref;
            // Marquer le parent link comme traité aussi
            processedElements.add(parentLink);
          }
        }
      }
      
      // Si on a trouvé une URL YouTube, créer l'iframe
      if (youtubeUrl && extractYouTubeId(youtubeUrl)) {
        const embedUrl = convertYouTubeToEmbed(youtubeUrl);
        if (embedUrl) {
          // Créer une iframe YouTube
          const iframe = document.createElement('iframe');
          iframe.setAttribute('src', embedUrl);
          iframe.setAttribute('width', '100%');
          iframe.setAttribute('height', '400');
          iframe.setAttribute('frameborder', '0');
          iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
          iframe.setAttribute('allowfullscreen', 'true');
          iframe.style.maxWidth = '100%';
          iframe.style.aspectRatio = '16 / 9';
          
          // Créer un conteneur responsive
          const container = document.createElement('div');
          container.style.position = 'relative';
          container.style.width = '100%';
          container.style.paddingBottom = '56.25%'; // 16:9 aspect ratio
          container.style.overflow = 'hidden';
          container.style.marginBottom = '1rem';
          iframe.style.position = 'absolute';
          iframe.style.top = '0';
          iframe.style.left = '0';
          iframe.style.width = '100%';
          iframe.style.height = '100%';
          
          container.appendChild(iframe);
          
          // Remplacer l'élément (ou son parent si c'est une image dans un lien) par l'iframe
          const elementToReplace = element.tagName === 'IMG' && element.closest('a') 
            ? element.closest('a') 
            : element;
          
          if (elementToReplace && elementToReplace.parentNode) {
            elementToReplace.parentNode.replaceChild(container, elementToReplace);
            processedElements.add(container);
          }
        }
      }
    });

    // RETOURNER LE HTML MODIFIÉ
    return tempDiv.innerHTML;
    
  } catch (error) {
    // GESTION D'ERREUR
    console.error('[processVideoEmbeds] Erreur lors du traitement des vidéos:', error);
    return htmlContent; // Retourner le contenu original en cas d'erreur
  }
};

/**
 * Fonction principale qui traite le contenu HTML complet
 * - Convertit les URLs YouTube en iframes
 * - Configure les liens pour s'ouvrir dans un nouvel onglet
 * 
 * @param {string} htmlContent - Le contenu HTML à traiter
 * @returns {string} - Le contenu HTML traité
 */
export const processHtmlContent = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return htmlContent;
  }
  
  // D'abord convertir les vidéos, puis traiter les liens
  let processed = processVideoEmbeds(htmlContent);
  processed = processLinksForNewTab(processed);
  
  return processed;
}; 